import type { BadgeTone } from '@/components/ui/badge'

// Single source of truth for Stripe subscription status badges across the
// backoffice (agencies, campaigns, affiliates). Previously duplicated as a
// per-page STATUS_CONFIG with raw className strings.
// Keys are the BillingFilter values the API computes (see back-end-next
// src/lib/lifecycle.ts). They are derived state, not the raw Stripe status: a
// cancelled agency has no Stripe status left to read once the downgrade lands.
export const BILLING_STATUS_CONFIG: Record<string, { label: string; tone: BadgeTone; tooltip: string }> = {
  paying: { label: 'Active', tone: 'success', tooltip: 'Subscription is active and payments are up to date' },
  trialing: { label: 'Trialing', tone: 'info', tooltip: 'Agency is in a free trial period' },
  cancelling: { label: 'Cancelling', tone: 'warning', tooltip: 'Cancellation scheduled, subscription runs until the period ends' },
  cancelled: { label: 'Cancelled', tone: 'danger', tooltip: 'Paid before and was automatically downgraded to free' },
  trial_expired: { label: 'Trial Expired', tone: 'danger', tooltip: 'Trial ended without ever converting to a paid plan' },
  past_due: { label: 'Past Due', tone: 'danger', tooltip: 'Payment failed, invoice is past due' },
  incomplete: { label: 'Incomplete', tone: 'attention', tooltip: 'Checkout started but payment was not confirmed' },
  free: { label: 'Free', tone: 'neutral', tooltip: 'Chose the free plan, no Stripe subscription' },
  manual: { label: 'Manual', tone: 'accent', tooltip: 'Paid plan with no live Stripe subscription' },
  not_selected: { label: 'Not selected', tone: 'neutral', tooltip: 'Never finished onboarding, so no plan was ever chosen' },
}

// Churn refinement, shown only once a churn status/stage is picked. Splits the
// two buckets that mix kinds of churn: "Cancelled" lumps genuine paid churn in
// with churn predating platform events (2026-04-25), and "Cancelling" covers
// trial and paid alike until the period ends.
export const CHURN_TYPE_CONFIG: Record<string, { label: string; tooltip: string }> = {
  trial_churn: { label: 'Trial churn', tooltip: 'Started a trial and left without ever being charged' },
  paid_churn: { label: 'Paid churn', tooltip: 'Was charged at least once, then cancelled' },
  churn_unknown: { label: 'Unknown (pre-Apr 2026)', tooltip: 'Churned before we recorded the events that tell trial from paid' },
  cancelling_trial: { label: 'Cancelling: was trialing', tooltip: 'Cancellation scheduled, never charged' },
  cancelling_paid: { label: 'Cancelling: was paying', tooltip: 'Cancellation scheduled, was charged at least once' },
}

/** Churn types that make sense for a given status/stage selection. */
export const CHURN_TYPES_FOR = (status?: string, stage?: string): string[] => {
  const churned = status === 'cancelled' || status === 'trial_expired' || stage === 'churned'
  const churning = status === 'cancelling' || stage === 'churning'
  return [
    ...(churned ? ['trial_churn', 'paid_churn', 'churn_unknown'] : []),
    ...(churning ? ['cancelling_trial', 'cancelling_paid'] : []),
  ]
}

// Engagement ladder, mirroring the lifecycle stage synced to the CRM.
export const LIFECYCLE_STAGE_CONFIG: Record<string, { label: string; tone: BadgeTone; tooltip: string }> = {
  'signed_up:wizard': { label: 'Signup: Wizard', tone: 'neutral', tooltip: 'Still inside the signup wizard' },
  'signed_up:plans': { label: 'Signup: Plans', tone: 'neutral', tooltip: 'Reached plan selection, has not chosen one' },
  'signed_up:checkout': { label: 'Signup: Checkout', tone: 'attention', tooltip: 'Reached checkout without completing it' },
  onboarded: { label: 'Onboarded', tone: 'info', tooltip: 'Finished onboarding but has not built anything yet' },
  activated: { label: 'Activated', tone: 'info', tooltip: 'Created a form, subaccount, integration or embed' },
  value_realized: { label: 'Value Realized', tone: 'success', tooltip: 'Captured a first lead or booked a first appointment' },
  power_user: { label: 'Power User', tone: 'success', tooltip: 'Started a split test or invited a teammate' },
  churning: { label: 'Churning', tone: 'warning', tooltip: 'Cancellation scheduled, still active until the period ends' },
  churned: { label: 'Churned', tone: 'danger', tooltip: 'Paid before and was automatically downgraded to free' },
}

// Trial risk flags, scored live from what a trial has produced so far. Keys are
// the TrialRiskLevel values the API computes (see back-end-next
// src/lib/admin/trial-risk.ts). Only trialing agencies carry one.
export const TRIAL_RISK_CONFIG: Record<string, { label: string; tone: BadgeTone; tooltip: string }> = {
  high_risk: { label: 'At risk: no leads', tone: 'danger', tooltip: "Form isn't producing anything (under 3 completed submissions)" },
  medium_risk: { label: 'At risk: no bookings', tone: 'warning', tooltip: 'Leads come in but nobody books' },
  healthy: { label: 'On track', tone: 'success', tooltip: 'Leads come in and at least one booked' },
}

/**
 * Map a raw Stripe subscription status onto a BILLING_STATUS_CONFIG key, for the
 * views that only have the raw column to hand. A null status cannot be told apart
 * from a completed cancellation without hasPaidBefore, so it reads as 'free';
 * prefer the API's computed billingStatus wherever one is returned.
 */
export function billingKeyFromStripeStatus(
  stripeStatus: string | null,
  cancelAtPeriodEnd = false,
): string {
  if (cancelAtPeriodEnd && (stripeStatus === 'active' || stripeStatus === 'trialing')) {
    return 'cancelling'
  }
  switch (stripeStatus) {
    case 'active': return 'paying'
    case 'trialing': return 'trialing'
    case 'past_due':
    case 'unpaid': return 'past_due'
    case 'incomplete': return 'incomplete'
    case 'canceled': return 'cancelled'
    default: return 'free'
  }
}

/** Team member status → tone (active / pending / other). */
export function memberStatusTone(status?: string | null): BadgeTone {
  switch (status) {
    case 'active':
      return 'success'
    case 'pending':
      return 'warning'
    default:
      return 'neutral'
  }
}
